import axios from 'axios';

type RevolutSetupApiResponse = {
  link?: string;
  url?: string;
  token?: string;
  checkout_token?: string;
  publicKey?: string;
  public_key?: string;
  orderId?: string;
  order_id?: string;
  payment_method_id?: string;
  [key: string]: unknown;
};

export type CreateRevolutSetupRequest = {
  payment_processor: 'revolut';
};

export type CreateRevolutSetupResponse = {
  token: string;
  link?: string;
  publicKey?: string;
  orderId?: string;
  mode?: 'sandbox' | 'prod';
  payment_method_id?: string;
  [key: string]: unknown;
};

export type RevolutSetupResponse = CreateRevolutSetupResponse;

type CreateRevolutPaymentSetupArgs = {
  companyHsId: number;
  cancelUrl?: string;
  successUrl?: string;
  signal?: AbortSignal;
  payload?: Partial<CreateRevolutSetupRequest>;
};

const getSetupEndpoint = () => {
  const billingService = process.env.REACT_APP_BILLING_SERVICE || '';

  if (billingService.includes('/billing/v1')) {
    return `${billingService}/payment/setup-auto-payment`;
  }

  return `${billingService}/billing/v1/payment/setup-auto-payment`;
};

const getRevolutModeFromEnv = (): 'sandbox' | 'prod' => {
  const env = process.env.REACT_APP_REVOLUT_ENV;

  return env === 'production' ? 'prod' : 'sandbox';
};

export const createRevolutPaymentSetup = async ({
  companyHsId,
  cancelUrl,
  successUrl,
  signal,
  payload,
}: CreateRevolutPaymentSetupArgs): Promise<CreateRevolutSetupResponse> => {
  const { data } = await axios.post<RevolutSetupApiResponse>(
    getSetupEndpoint(),
    {
      ...payload,
      payment_processor: 'revolut',
      company_hs_id: companyHsId,
      cancel_url: cancelUrl,
      success_url: successUrl,
    },
    {
      signal,
    }
  );

  const token = data.token || data.checkout_token;
  const link = data.link || data.url;
  const publicKey = data.publicKey || data.public_key;
  const orderId = data.orderId || data.order_id;
  const mode = getRevolutModeFromEnv();
  const paymentMethodId = data.payment_method_id;

  if (!token) {
    throw new Error('Invalid Revolut setup response');
  }

  return {
    ...data,
    token,
    link,
    publicKey,
    orderId,
    mode,
    payment_method_id: paymentMethodId,
  };
};

export const fetchPaymentMethodDetails = async (
  paymentMethodId: string,
  signal?: AbortSignal
): Promise<PMethod> => {
  const billingService = process.env.REACT_APP_BILLING_SERVICE || '';
  const endpoint = billingService.includes('/billing/v1')
    ? `${billingService}/payment/payment-methods/${paymentMethodId}`
    : `${billingService}/billing/v1/payment/payment-methods/${paymentMethodId}`;

  const { data } = await axios.get<PMethod>(endpoint, { signal });
  return data;
};

type ChallengeResponse = {
  type: string;
  url: string;
};

export const fetchChallengeUrl = async (
  paymentMethodId: string,
  signal?: AbortSignal
): Promise<string | null> => {
  const billingService = process.env.REACT_APP_BILLING_SERVICE || '';
  const endpoint = billingService.includes('/billing/v1')
    ? `${billingService}/payment/payment-methods/${paymentMethodId}/challenge`
    : `${billingService}/billing/v1/payment/payment-methods/${paymentMethodId}/challenge`;

  try {
    const { data } = await axios.get<ChallengeResponse>(endpoint, { signal });
    return data.url || null;
  } catch {
    return null;
  }
};
